import { useMount } from 'ahooks';
import { Dropdown, Empty, Form, Input, Tooltip } from 'antd';
import { useAppDispatch } from 'app/hooks';
import { RevxSkeleton } from 'components';
import { CalendarIcon } from 'components/Icons';
import { RevxButton } from 'components/RevxButton';
import {
  RFQ_SHIPPING_DATE_FORMAT_LOCAL,
  RevxDate,
  SHIPPING_DATE_FORMAT,
} from 'components/RevxDate';
import RevxFlexLayout from 'components/RevxFlexLayout';
import { toast } from 'components/RevxMessage';
import { RevxMoney } from 'components/RevxMoney';
import { RevxOverlay } from 'components/RevxOverlay';
import { RevxTrackingInfo } from 'components/RevxTrackingInfo';
import { CarrierTypeEnum } from 'domains/shippingDomain';
import {
  JobShippingItemFulfillmentUpdateRequest,
  JobShippingSummaryItem,
  Summary,
} from 'domains/workspaceDetailDomain';
import { JobItem, JobWorkflowStatusEnum } from 'domains/workspaceDomain';
import { RevxJobConfigCalendar } from 'features/Workspace/components/RevxJobConfigCalendar';
import { clone, isEmpty } from 'lodash-es';
import moment from 'moment';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import workspaceService from 'services/jobService';
import workspaceDetailService from 'services/workspaceDetailService';
import {
  INPUT_NORMAL_MAX_LENGTH,
  INTERNAL_DISTRIBUTION,
  SHIPPING_WILL_CALL,
  WORKSPACE_DELAY_TIME,
} from 'utils/constants';
import { dateToWeek } from 'utils/date';
import { sleep } from 'utils/misc';
import InternalDistributionBlock from '../../components/InternalDistributionBlock';
import './SupplierShipping.less';

const { Nonstandard, UPS, FedEx } = CarrierTypeEnum;

const SupplierShipping = (jobData: JobItem) => {
  const [listForm] = Form.useForm();

  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [items, setItems] = useState<JobShippingSummaryItem[]>([]);

  const [itemEditStatuses, setItemEditStatuses] = useState<boolean[]>([]);
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState<Partial<Summary>>({});

  const trackingInfoShow = [
    JobWorkflowStatusEnum.IN_PRODUCTION,

    JobWorkflowStatusEnum.SHIPPED,
    JobWorkflowStatusEnum.DELIVERED,

    JobWorkflowStatusEnum.INVOICING,
  ];

  useMount(async () => {
    setLoading(true);
    try {
      const summaryRes = await workspaceDetailService.getSummary(jobData.id);
      setSummary(summaryRes);

      const res: JobShippingSummaryItem[] = await getJobShippingItems();

      listForm.setFieldsValue({
        formItems: res,
      });

      // init edit status state with a matching length boolean array
      const editStatuses = new Array<boolean>(res?.length).fill(false);
      setItemEditStatuses(editStatuses);
    } catch {}

    setLoading(false);
  });

  const getJobShippingItems = async () => {
    const res = await workspaceService.getJobShippingItems(jobData.id);
    setItems(res);
    return res;
  };

  const toggleItemEditStatus = (idx: number) => {
    const clonedStatuses = clone(itemEditStatuses);
    clonedStatuses[idx] = !clonedStatuses[idx];
    setItemEditStatuses(clonedStatuses);
  };

  const onItemEditFinish = async (idx: number) => {
    await dispatch.app.showLoading();
    toggleItemEditStatus(idx);

    try {
      const formData = await listForm.validateFields([
        ['formItems', idx, 'shippingInfo', 'trackingCode'],
      ]);
      const updateRequest: JobShippingItemFulfillmentUpdateRequest = {
        trackingCode: formData?.formItems[idx].shippingInfo.trackingCode,
      };
      const updated = await workspaceService.updateJobShippingItemFulfillment(
        jobData.id,
        items[idx].id,
        updateRequest
      );
      toast(t('toast.supplierTrackingCodeEntered'));
      const clonedShippings = clone(items);
      clonedShippings[idx] = updated;
      if (
        !clonedShippings.find((shipping) => !shipping.shippingInfo.trackingCode)
      ) {
        await sleep(WORKSPACE_DELAY_TIME);
        dispatch.workspace.reloadJobList();
      }
      setItems(clonedShippings);
    } catch (error) {
      listForm.setFieldsValue({
        formItems: items,
      });
    } finally {
      await dispatch.app.hideLoading();
    }
  };
  const checkAllShippingItemFinishStatus = (): boolean => {
    return !(items.filter((i) => !i.actualShipTime).length > 1);
  };
  const updateNonstandardShipStatus = async (item: JobShippingSummaryItem) => {
    await dispatch.app.showLoading();
    try {
      await workspaceService.updateJobShippingItemFulfillmentNonstandard(
        jobData.id,
        item.id,
        {
          shipDate: item.readyDate,
          shippingDetails: item.shippingDetail,
        }
      );
      await dispatch.app.hideLoading();
      await getJobShippingItems();
      if (checkAllShippingItemFinishStatus()) {
        await sleep(WORKSPACE_DELAY_TIME);
        await dispatch.workspace.reloadJobList();
      }
    } catch {
      await dispatch.app.hideLoading();
    }
  };
  const changeShipInfo = (
    item: JobShippingSummaryItem,
    {
      readyDate,
      shippingDetail = '',
    }: { readyDate?: string; shippingDetail?: string }
  ) => {
    const cloneItems = clone(items);
    cloneItems.forEach((i, index) => {
      if (i.id === item.id) {
        cloneItems[index] = {
          ...i,
          ...{
            readyDate: readyDate ? readyDate : i.readyDate,
            shippingDetail,
          },
        };
      }
    });
    setItems(cloneItems);
  };
  const nonstandardShipDateBlock = (item: JobShippingSummaryItem) => {
    const {
      shippingInfo: { carrierService, carrierType },
      actualShipTime,
    } = item;
    const isWillCall = carrierService === SHIPPING_WILL_CALL;
    const isInternalDistribution = carrierService === INTERNAL_DISTRIBUTION;

    const isShipped = actualShipTime;
    if (carrierType === Nonstandard) {
      return (
        <RevxFlexLayout
          justify="justify-center"
          align="start"
          className={`shipping-item-shipped ${
            !isShipped ? '' : 'nonstandard-ship-item'
          }`}
        >
          <div className="shipping-item-shipped__info-block">
            <div>
              {jobData.workflowStatus !== JobWorkflowStatusEnum.IN_PRODUCTION &&
              !isShipped ? (
                <div>
                  {!isShipped ? (
                    <label>{t('workspace.list.estimatedShipDate')}:</label>
                  ) : (
                    <label>{t('workspace.list.dateShipped')}:</label>
                  )}
                  <div>{dateToWeek(item.shippingInfo.estimatedShipTime)}</div>
                  <RevxDate
                    dateStr={item.shippingInfo.estimatedShipTime}
                    formatStr={SHIPPING_DATE_FORMAT}
                  />
                  , {carrierType}, {carrierService}
                </div>
              ) : isShipped ? (
                <div className="ship-date-info-block">
                  {isWillCall ? (
                    <>
                      <div>
                        <p>{t('workspace.list.datePickedUp')}:&nbsp;</p>
                        <span>
                          {moment(
                            item.actualDeliveryTime ||
                              item.estimatedDeliveryTime
                          ).format(SHIPPING_DATE_FORMAT)}
                        </span>
                      </div>
                      <div>
                        <p>{t('workspace.list.shippingInfo')}:&nbsp;</p>
                        <span>{SHIPPING_WILL_CALL}</span>
                      </div>
                    </>
                  ) : isInternalDistribution ? (
                    <>
                      <div>
                        <p>{t('workspace.list.datePickedUp')}:&nbsp;</p>
                        <span>
                          {moment(
                            item.actualDeliveryTime ||
                              item.estimatedDeliveryTime
                          ).format(SHIPPING_DATE_FORMAT)}
                        </span>
                      </div>
                      <div>
                        <p>{t('workspace.list.shippingInfo')}:&nbsp;</p>
                        <span>{INTERNAL_DISTRIBUTION}</span>
                      </div>
                    </>
                  ) : (
                    <>
                      <div>
                        <p>{t('workspace.list.shipDate')}:&nbsp;</p>
                        <span>
                          {moment(item.actualShipTime).format(
                            SHIPPING_DATE_FORMAT
                          )}
                        </span>
                      </div>
                      <div>
                        <p>{t('workspace.list.shippingDetails')}:&nbsp;</p>
                        <Tooltip title={item.shippingDetail}>
                          <span className="shipping-details-wrap">
                            {item.shippingDetail || '--'}
                          </span>
                        </Tooltip>
                      </div>
                    </>
                  )}
                </div>
              ) : (
                <>
                  <div className="nonstandard-block">
                    <RevxFlexLayout align="center" className="">
                      <p className="ship-date-item">
                        <span className="ship-date-label-color">
                          {isWillCall || isInternalDistribution
                            ? 'Ready Date'
                            : 'Ship Date'}
                        </span>
                        : {moment(item?.readyDate).format(SHIPPING_DATE_FORMAT)}
                      </p>
                      <Dropdown
                        trigger={['click']}
                        overlay={
                          <RevxOverlay style={{ padding: 20 }}>
                            <RevxJobConfigCalendar
                              color="green"
                              value={moment(item.readyDate)}
                              onChange={(date) => {
                                changeShipInfo(item, {
                                  readyDate: moment(date).format(
                                    RFQ_SHIPPING_DATE_FORMAT_LOCAL
                                  ),
                                });
                              }}
                            />
                          </RevxOverlay>
                        }
                      >
                        <p>
                          <CalendarIcon className="cal" />
                        </p>
                      </Dropdown>
                    </RevxFlexLayout>
                    {isWillCall || isInternalDistribution ? (
                      <RevxButton
                        className="ready-pickup-btn"
                        type="primary"
                        onClick={() => updateNonstandardShipStatus(item)}
                      >
                        {t('workspace.detail.supplierShipping.readyForPickup')}
                      </RevxButton>
                    ) : (
                      <RevxFlexLayout className="shipment-input">
                        <Input
                          placeholder="Shipment details here"
                          maxLength={INPUT_NORMAL_MAX_LENGTH}
                          value={item.shippingDetail}
                          onChange={(v) => {
                            changeShipInfo(item, {
                              shippingDetail: v.target.value,
                            });
                          }}
                        />
                        <RevxButton
                          className="submit-btn"
                          type="primary"
                          onClick={async () => {
                            await updateNonstandardShipStatus(item);
                          }}
                        >
                          Submit
                        </RevxButton>
                      </RevxFlexLayout>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </RevxFlexLayout>
      );
    }
  };

  const trackingInfoDisplay = (item: JobShippingSummaryItem) => {
    const {
      actualShipTime,
      shippingInfo: { carrierType, trackingCode },
    } = item;
    return {
      btn:
        [UPS, FedEx].includes(carrierType) && !actualShipTime && !trackingCode,
      info:
        [UPS, FedEx].includes(carrierType) &&
        ((actualShipTime && trackingCode) ||
          (!actualShipTime && !trackingCode)),
    };
  };

  return (
    <>
      {items?.length > 0 ? (
        <div className="supplier-billing-card">
          <div className="supplier-billing-card-header">
            <hgroup>
              <span>{t('workspace.detail.shippingBilling.shipping')}</span>
            </hgroup>
          </div>

          <Form form={listForm} className="shipping">
            <Form.List name="formItems">
              {(fields) => (
                <>
                  {fields.map((field) => (
                    <div
                      className="shipping-item"
                      key={items[field.fieldKey].id}
                    >
                      <div className="shipping-item-title">
                        {items[field.fieldKey].name}
                      </div>
                      <div className="shipping-item-quantity">
                        <label>
                          {t('workspace.detail.shippingBilling.quantity')}:
                        </label>
                        <p>{items[field.fieldKey].quantity}</p>
                      </div>
                      <div className="shipping-item-address">
                        <label>
                          {t(
                            'workspace.detail.shippingBilling.sendToNameAddress'
                          )}
                          :
                        </label>
                        <p>{items[field.fieldKey]?.shippingInfo?.toName}</p>
                        <p>
                          {[
                            items[field.fieldKey]?.shippingInfo?.toAddressLine,
                            items[field.fieldKey]?.shippingInfo?.toCity,
                            items[field.fieldKey].shippingInfo
                              ?.toStateProvinceCode,
                            items[field.fieldKey].shippingInfo?.toPostalCode,
                            items[field.fieldKey].shippingInfo?.toCountryCode,
                          ]
                            .filter((item) => item)
                            .join(', ')}
                        </p>
                        <InternalDistributionBlock
                          jobData={jobData}
                          item={items[field.fieldKey]}
                        />
                      </div>
                      {items[field.fieldKey].shippingInfo.carrierType !==
                        Nonstandard && (
                        <div className="shipping-item-shipped">
                          <div className="shipping-item-shipped__info-block">
                            {isEmpty(
                              items[field.fieldKey].shippingInfo.trackingCode
                            ) && !items[field.fieldKey].actualShipTime ? (
                              <>
                                <label>
                                  {t('workspace.list.estimatedShipDate')}:
                                </label>
                                <p>
                                  <div>
                                    {dateToWeek(
                                      items[field.fieldKey].shippingInfo
                                        .estimatedShipTime
                                    )}
                                  </div>
                                  <Form.Item
                                    {...field}
                                    key={`actualShipTime${field.key}`}
                                    noStyle
                                    label={t(
                                      'workspace.detail.shippingBilling.formFreight'
                                    )}
                                    name={[field.name, 'estimatedShipTime']}
                                    fieldKey={[
                                      field.fieldKey,
                                      'estimatedShipTime',
                                    ]}
                                  >
                                    <RevxDate
                                      dateStr={
                                        items[field.fieldKey].shippingInfo
                                          .estimatedShipTime
                                      }
                                      formatStr={SHIPPING_DATE_FORMAT}
                                    />
                                    ,{' '}
                                  </Form.Item>
                                  <Form.Item
                                    {...field}
                                    key={`carrierService${field.key}`}
                                    noStyle
                                    label={t(
                                      'workspace.detail.shippingBilling.formFreight'
                                    )}
                                    name={[field.name, 'carrierService']}
                                    fieldKey={[
                                      field.fieldKey,
                                      'carrierService',
                                    ]}
                                  >
                                    {
                                      items[field.fieldKey].shippingInfo
                                        .carrierType
                                    }
                                    ,{' '}
                                    {
                                      items[field.fieldKey].shippingInfo
                                        .carrierService
                                    }
                                  </Form.Item>
                                </p>
                              </>
                            ) : (
                              <>
                                <label>
                                  {t(
                                    'workspace.detail.shippingBilling.dateShipped'
                                  )}
                                  :
                                </label>
                                <div>
                                  {dateToWeek(
                                    items[field.fieldKey].actualShipTime
                                  )}
                                </div>
                                <Form.Item
                                  {...field}
                                  key={`actualShipTime${field.key}`}
                                  noStyle
                                  label={t(
                                    'workspace.detail.shippingBilling.formFreight'
                                  )}
                                  name={[field.name, 'actualShipTime']}
                                  fieldKey={[field.fieldKey, 'actualShipTime']}
                                >
                                  <p>
                                    <RevxDate
                                      dateStr={
                                        items[field.fieldKey].actualShipTime
                                      }
                                      formatStr={SHIPPING_DATE_FORMAT}
                                    />
                                    &nbsp;
                                  </p>
                                </Form.Item>
                              </>
                            )}
                          </div>
                          {trackingInfoShow.find(
                            (status) => jobData.workflowStatus === status
                          ) &&
                            trackingInfoDisplay(items[field.fieldKey]).info && (
                              <div>
                                <Form.Item
                                  style={{ marginBottom: 0 }}
                                  {...field}
                                  key={`trackingCode${field.key}`}
                                  label={t(
                                    'workspace.detail.shippingBilling.formFreight'
                                  )}
                                  rules={[{ required: true }]}
                                  name={[
                                    field.name,
                                    'shippingInfo',
                                    'trackingCode',
                                  ]}
                                  fieldKey={[field.fieldKey, 'trackingCode']}
                                >
                                  <RevxTrackingInfo
                                    carrierType={
                                      items[field.fieldKey].shippingInfo
                                        .carrierType || UPS
                                    }
                                    editable
                                    defaultEmptyEditing
                                    onChange={(val) => {
                                      onItemEditFinish(field.fieldKey);
                                    }}
                                  />
                                </Form.Item>
                                {trackingInfoDisplay(items[field.fieldKey])
                                  .btn && (
                                  <RevxButton
                                    className="skip-tracking-btn"
                                    onClick={async () => {
                                      await dispatch.app.showLoading();
                                      await workspaceService.skipForTrackNumber(
                                        jobData.id,
                                        items[field.fieldKey].id
                                      );
                                      await sleep(WORKSPACE_DELAY_TIME);
                                      await dispatch.app.hideLoading();
                                      await dispatch.workspace.reloadJobList();
                                    }}
                                  >
                                    {t(
                                      'workspace.detail.shippingBilling.shipWithoutTracking'
                                    )}
                                  </RevxButton>
                                )}
                              </div>
                            )}
                        </div>
                      )}
                      {nonstandardShipDateBlock(items[field.fieldKey])}

                      <div className="shipping-item-account">
                        <label>
                          {t('workspace.detail.proofs.shipAccount')}:
                        </label>
                        <p>{items[field.fieldKey].shippingInfo.account}</p>
                      </div>
                    </div>
                  ))}
                </>
              )}
            </Form.List>
          </Form>

          <div className="tip">
            <div className="tip-item">
              <label>{t('workspace.detail.shippingBilling.freight')}: </label>
              <span>
                <RevxMoney value={summary?.supplierFreightCharge || 0} />
              </span>
            </div>
            <div className="tip-item">
              <label>{t('workspace.detail.shippingBilling.orderTotal')}:</label>
              <span>
                <RevxMoney value={summary?.supplierCharge || 0} />
              </span>
            </div>
          </div>
        </div>
      ) : (
        <RevxSkeleton loading={loading}>
          <Empty />
        </RevxSkeleton>
      )}
    </>
  );
};

export default SupplierShipping;
